// Cambios de referencia (coordinador; directivo en lectura).
//
// Un episodio agrupa paros consecutivos (separación ≤ 5 min) de una línea cuya
// categoría está marcada como cambio de referencia. Se compara el tiempo real
// con el estándar de la línea y se muestra el SKU saliente → entrante
// (códigos del cliente). Un episodio con un paso abierto está "en curso": sin
// fin ni minutos reales.

import { Navigate } from 'react-router-dom';
import type { CambioReferenciaEpisodio } from 'schemas';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent } from '../../../components/ui/card';
import { Icon } from '../../../components/ui/icon';
import { KpiCard } from '../../../components/ui/kpi-card';
import { Spinner } from '../../../components/ui/spinner';
import { CoordinadorSidebar } from '../../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../../components/layout/RoleLayout';
import { useCambiosReferencia } from '../../../lib/api/ingesta';
import { useAuth } from '../../../lib/auth';
import { cn } from '../../../lib/utils';
import { horaParo } from '../../captura/BannerParosPendientesCausal';
import { errorRango, FiltroRango, useFiltrosRango } from './FiltroRango';
import { useOpcionesConectadas } from './useOpcionesConectadas';

function minutos(n: number | null): string {
  if (n === null) return '—';
  return `${Math.round(n)} min`;
}

/** Tono de la diferencia real − estándar: más lento es malo. */
export function tonoDiferencia(dif: number | null): 'ok' | 'warn' | 'crit' | 'neutral' {
  if (dif === null) return 'neutral';
  if (dif <= 0) return 'ok';
  if (dif <= 10) return 'warn';
  return 'crit';
}

const CLASE_TONO = {
  ok: 'text-ok',
  warn: 'text-warn',
  crit: 'text-crit',
  neutral: 'text-fg-tertiary',
} as const;

export function CambiosReferenciaPage() {
  const { state } = useAuth();
  const [filtros, setFiltros] = useFiltrosRango();
  const opciones = useOpcionesConectadas();
  const rangoInvalido = errorRango(filtros.desde, filtros.hasta) !== null;
  const q = useCambiosReferencia(
    {
      desde: filtros.desde,
      hasta: filtros.hasta,
      ...(filtros.lineaId ? { linea_id: filtros.lineaId } : {}),
    },
    { enabled: !rangoInvalido },
  );

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  // El endpoint filtra por línea, no por planta: la planta se aplica aquí.
  const lineasPlanta = filtros.plantaId
    ? new Set(opciones.lineas.filter((l) => l.plantaId === filtros.plantaId).map((l) => l.id))
    : null;
  const episodios = (q.data?.episodios ?? []).filter(
    (e) => !lineasPlanta || lineasPlanta.has(e.linea_id),
  );

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="flex flex-col gap-3 border-b border-border pb-3">
          <div>
            <h1 className="text-xl font-medium text-fg">Cambios de referencia</h1>
            <p className="mt-1 text-[12px] text-fg-tertiary">
              Cada cambio de SKU en la línea, paso a paso, contra el estándar de la línea.
            </p>
          </div>
          <FiltroRango
            filtros={filtros}
            onChange={setFiltros}
            plantas={opciones.plantas}
            lineas={opciones.lineas}
          />
        </header>
        {rangoInvalido ? null : q.isLoading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-fg-secondary">
            <Spinner className="size-4" /> Cargando…
          </div>
        ) : q.isError ? (
          <p role="alert" className="rounded bg-crit-soft px-3 py-2 text-sm text-crit">
            {q.error instanceof Error ? q.error.message : 'No pudimos cargar los cambios'}
          </p>
        ) : (
          <CambiosReferenciaVista episodios={episodios} />
        )}
      </div>
    </RoleLayout>
  );
}

export function CambiosReferenciaVista({ episodios }: { episodios: CambioReferenciaEpisodio[] }) {
  if (episodios.length === 0) {
    return (
      <div className="rounded border border-dashed border-border px-4 py-10 text-center">
        <p className="text-sm font-medium text-fg">Sin cambios de referencia en el período</p>
        <p className="mx-auto mt-1 max-w-md text-[12px] text-fg-tertiary">
          Se cuentan los paros cuya categoría está marcada como cambio de referencia en la
          configuración de causales.
        </p>
      </div>
    );
  }
  const cerrados = episodios.filter((e) => e.minutos_reales !== null);
  const totalReal = cerrados.reduce((a, e) => a + (e.minutos_reales ?? 0), 0);
  const conEstandar = cerrados.filter((e) => e.diferencia_minutos !== null);
  const difProm =
    conEstandar.length > 0
      ? conEstandar.reduce((a, e) => a + (e.diferencia_minutos ?? 0), 0) / conEstandar.length
      : null;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="grid grid-cols-3 gap-y-4 py-4">
          <KpiCard
            label="Cambios"
            value={episodios.length}
            sub={
              episodios.length > cerrados.length
                ? `${episodios.length - cerrados.length} en curso`
                : 'todos cerrados'
            }
          />
          <KpiCard
            label="Tiempo en cambios"
            value={Math.round(totalReal)}
            valueSuffix="min"
            divider
          />
          <KpiCard
            label="Diferencia promedio"
            hint="Real − estándar de la línea, sobre los cambios cerrados con estándar."
            value={difProm === null ? '—' : `${difProm > 0 ? '+' : ''}${Math.round(difProm)}`}
            valueSuffix={difProm === null ? undefined : 'min'}
            sub={difProm === null ? 'sin estándar configurado' : 'por cambio'}
            subTone={tonoDiferencia(difProm)}
            divider
          />
        </CardContent>
      </Card>
      <ol className="flex flex-col gap-3">
        {episodios.map((e, i) => (
          <EpisodioCard key={`${e.linea_id}-${e.inicio}-${i}`} e={e} />
        ))}
      </ol>
    </div>
  );
}

function EpisodioCard({ e }: { e: CambioReferenciaEpisodio }) {
  const enCurso = e.minutos_reales === null;
  const tono = tonoDiferencia(e.diferencia_minutos);
  const max = Math.max(e.minutos_reales ?? 0, e.minutos_estandar ?? 0, 1);
  return (
    <li>
      <Card className={cn(enCurso && 'border-l-2 border-l-warn')}>
        <CardContent className="flex flex-col gap-3 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-fg">
                {e.linea_nombre}
                <span className="font-normal text-fg-tertiary">
                  {' '}
                  · {e.fecha} · {horaParo(e.inicio)}
                  {e.fin ? `–${horaParo(e.fin)}` : ''}
                </span>
              </p>
              <p className="mt-1 inline-flex items-center gap-2 font-mono text-[13px] text-fg-secondary">
                <span>{e.sku_saliente ?? '—'}</span>
                <Icon name="arrows-exchange" size={14} className="text-fg-tertiary" />
                <span className="text-fg">{e.sku_entrante ?? '—'}</span>
              </p>
            </div>
            {enCurso ? (
              <Badge tone="warn">En curso</Badge>
            ) : (
              <div className="text-right">
                <p
                  className={cn('text-2xl font-medium leading-none tabular-nums', CLASE_TONO[tono])}
                >
                  {e.diferencia_minutos === null
                    ? '—'
                    : `${e.diferencia_minutos > 0 ? '+' : ''}${Math.round(e.diferencia_minutos)} min`}
                </p>
                <p className="mt-1 text-[11px] text-fg-tertiary">vs estándar</p>
              </div>
            )}
          </div>

          {/* Real vs estándar: dos barras sobre la misma escala. */}
          <div className="grid grid-cols-[5.5rem_1fr_4rem] items-center gap-x-3 gap-y-1.5 text-[11px]">
            <span className="text-fg-tertiary">Real</span>
            <span className="h-2 overflow-hidden rounded-full bg-surface-overlay">
              <span
                className="block h-full rounded-full bg-fg-secondary"
                style={{ width: `${((e.minutos_reales ?? 0) / max) * 100}%` }}
              />
            </span>
            <span className="text-right tabular-nums text-fg">
              {enCurso ? 'en curso' : minutos(e.minutos_reales)}
            </span>
            <span className="text-fg-tertiary">Estándar</span>
            <span className="h-2 overflow-hidden rounded-full bg-surface-overlay">
              {e.minutos_estandar !== null ? (
                <span
                  className="block h-full rounded-full bg-border-stronger"
                  style={{ width: `${(e.minutos_estandar / max) * 100}%` }}
                />
              ) : null}
            </span>
            <span className="text-right tabular-nums text-fg-secondary">
              {e.minutos_estandar === null ? 'sin estándar' : minutos(e.minutos_estandar)}
            </span>
          </div>

          <ol className="flex flex-col divide-y divide-border rounded border border-border">
            {e.pasos.map((p, n) => (
              <li
                key={p.paro_id}
                className="flex items-center justify-between gap-3 px-3 py-1.5 text-[12px]"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="font-mono text-[10px] text-fg-tertiary">{n + 1}</span>
                  <span className="truncate text-fg">{p.causal_nombre}</span>
                </span>
                <span className="shrink-0 tabular-nums text-fg-secondary">
                  {horaParo(p.hora_inicio)}
                  {p.hora_fin ? `–${horaParo(p.hora_fin)}` : ' · abierto'} · {minutos(p.minutos)}
                </span>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>
    </li>
  );
}
